import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Separator } from '@/components/ui/separator';
import { GoogleLoginButton } from '@/components/auth/GoogleLoginButton';
import { EmailLoginForm } from '@/components/auth/EmailLoginForm';
import { LoadingSpinner } from '@/components/auth/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { AuthError } from '@supabase/supabase-js';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, user } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useAdminStatus();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && user && !isAdminLoading) {
      if (isSuperAdmin) {
        console.log('Super admin detected, redirecting to admin panel');
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, isSuperAdmin, isAdminLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError(null);

    try {
      const { data, error } = await signIn(email, password);
      
      if (error) throw error;
      
      if (data?.user) {
        // The useAdminStatus hook will handle the admin check and navigation
        toast.success('Successfully logged in');
      }
    } catch (error) {
      const authError = error as AuthError;
      console.error('Login error:', authError);
      setLoginError(authError.message || 'An error occurred during login');
      toast.error(authError.message || 'An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setLoginError('Please enter your email address');
      toast.error('Please enter your email address');
      return;
    }

    setIsResetting(true);
    setLoginError(null);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success('Password reset instructions have been sent to your email');
    } catch (error) {
      const authError = error as AuthError;
      console.error('Reset password error:', authError);
      setLoginError(authError.message || 'An error occurred while requesting password reset');
      toast.error(authError.message || 'An error occurred while requesting password reset');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSocialLogin = async (provider: 'google') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/application`,
        },
      });
      if (error) throw error;
    } catch (error) {
      const authError = error as AuthError;
      console.error('Social login error:', authError);
      toast.error(authError.message || `An error occurred during ${provider} login`);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleGoBack}
          className="absolute top-4 left-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src="/lovable-uploads/f7cec12b-685c-41ca-bf44-dda5fa89dc2e.png"
            alt="The PEO App Logo"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
        </div>

        {loginError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription>{loginError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <GoogleLoginButton onClick={() => handleSocialLogin('google')} />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gradient-to-br from-white to-green-50 px-2 text-muted-foreground">
                Or continue with email
              </span>
            </div>
          </div>
        </div>

        <EmailLoginForm
          email={email}
          password={password}
          isSubmitting={isSubmitting}
          isResetting={isResetting}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={handleSubmit}
          onResetPassword={handleResetPassword}
        />
      </div>
    </div>
  );
};

export default Login;
